import React from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { resolveWorkspace, PBDQ_WORKSPACE } from '../../../config/workspaceNav/solutions';
import { isClientWorkspace } from '../../../utils/capabilities';
import CompareArrowsOutlinedIcon from '@material-ui/icons/CompareArrowsOutlined';
import TuneOutlinedIcon from '@material-ui/icons/TuneOutlined';
import WidgetsOutlinedIcon from '@material-ui/icons/WidgetsOutlined';

/**
 * Solutions > LearnCreator > Overview — Common Section screen, same family
 * as the other pages in this folder. Layout/copy came from the reference
 * mockup (CS-Solution-LC-overview); styling follows this app's own tokens
 * per the standing instruction that solution overview/preview pages don't
 * need to match a mockup's exact look.
 *
 * "Create a Program" continues the EXISTING LearnCreator entry page
 * (DQProProgCreationPreviewV2.page.jsx at /dq_pro/program/creation/preview,
 * the real in-app chat-based program builder) directly, rather than a new
 * screen — same "continue the existing flow" pattern already used for
 * Solutions > COS Children Assessment > Preview. NOT /dq_pro/home: that page
 * is a guest/marketing teaser of the same idea and always redirects an
 * already-logged-in, non-guest user away from itself. "Manage School Access"
 * opens My Schools (LA-PW-14Oct-02), where schools are managed — for the
 * workspaces that have that page (Partner, School Network). A school has no
 * My Schools: it opens My Classes, where its teachers and classes are
 * (LA-PW-14Oct-02, SW). A teacher has neither, so it stays "Coming soon".
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';
const ORANGE = '#FF6900';

// Same content DQProProgCreationPreviewV2.page.jsx (the page this overview's
// "Preview LearnCreator" hands off to) is built around — repeated here so the
// summary is visible before a viewer commits to opening that page.
const WHATS_INCLUDED = [
    { title: 'Program & syllabus', desc: 'Complete program architecture, from syllabus to each lesson, built around COS skills.' },
    { title: 'AI content engine', desc: 'Classroom-ready slides, videos, visuals, and activities.' },
    { title: 'Embedded assessment', desc: 'Pre and post assessments that measure progress against the COS Assessment.' },
    { title: 'Style controls', desc: 'Adjustable graphic style and cultural context across the whole program.' },
];

const WAYS_TO_USE = [
    {
        title: 'Create Programs for Your Schools',
        desc: 'Build programs centrally and distribute them to selected schools, grades, or learner groups.',
        cta: 'Create a Program',
        // Step 0 of the flow: the person describes what they want to teach
        // there, and that page hands off to the chat builder.
        path: '/dq_pro/learncreator/start',
    },
    {
        title: 'Enable Schools to Create',
        desc: 'Give teachers access to LearnCreator so they can create and adapt programs for their own curriculum and classrooms.',
        cta: 'Manage School Access',
        path: '/dq_pro/clients/list',
        // Only where My Schools exists — the same rule the route guard applies.
        needsMySchools: true,
        // A school workspace: its own classes and teachers, so the button names
        // teachers (Lydia, 16 Oct).
        schoolPath: '/dq_pro/classes',
        schoolCta: 'Manage Teacher Access',
    },
];

const WHAT_YOU_CAN_CUSTOMIZE = [
    {
        title: 'Standards & Frameworks',
        desc: 'Align learning to relevant national or international frameworks.',
    },
    {
        title: 'Subjects & Curriculum',
        desc: 'Deliver learning as a standalone program or integrate it into existing subjects.',
    },
    {
        title: 'Cultural & Local Context',
        desc: 'Adapt language, examples, sensitivities, and context.',
    },
    {
        title: 'Learning Outcomes',
        desc: 'Focus the program on the capabilities learners most need to develop.',
    },
];

const primaryButtonSx = {
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    borderRadius: '50px',
    px: 2.5,
    py: 0.9,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#e55e00' },
};

const outlineButtonSx = {
    borderColor: BORDER,
    color: TEXT,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    borderRadius: '50px',
    px: 2.5,
    py: 0.9,
    whiteSpace: 'nowrap',
    '&.Mui-disabled': { borderColor: BORDER, color: MUTED },
};

const plainCardSx = {
    border: `1px solid ${BORDER}`,
    borderRadius: '14px',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
    backgroundColor: 'var(--brand-surface, #ffffff)',
};

const cardHeaderIconSx = {
    width: 36, height: 36, borderRadius: '10px',
    backgroundColor: 'rgba(255, 105, 0, 0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
};

const CardHeader = ({ icon: Icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2, mb: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={cardHeaderIconSx}>
            <Icon sx={{ fontSize: 20, color: ORANGE }} />
        </Box>
        <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT }}>
            {title}
        </Typography>
    </Box>
);

const SolutionLearnCreatorOverviewPage = () => {
    const history = useHistory();
    const { authUser } = useAuth();
    // My Schools is a Partner's page, and a School Network's (PrivateRouteWithLayout).
    /*
     * The route guard's own rule (PrivateRouteWithLayout): a school or network
     * workspace (isClientWorkspace — a partner's P1A school, and a direct P1B
     * school, which logs in at the partner's access level) has My Schools only
     * when it is a network. A direct school was read as a Partner here, sent to
     * My Schools, and bounced to the Homepage by the guard.
     */
    const isClientWs = isClientWorkspace(authUser);
    const hasMySchools = isClientWs
        ? !!authUser?.is_school_network
        : resolveWorkspace(authUser) === PBDQ_WORKSPACE.PARTNER;
    const isSchool = isClientWs && !authUser?.is_school_network;
    // Where a "way" goes for this user, or null when it has nowhere to go yet.
    const targetOf = (way) => {
        if (!way.path) return null;
        if (!way.needsMySchools || hasMySchools) return way.path;
        return isSchool && way.schoolPath ? way.schoolPath : null;
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
            <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1.5, maxWidth: '640px' }}>
                Create Customized Learning Programs, Faster
            </Typography>
            <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6, mb: 5, }}>
                LearnCreator helps you build complete learning programs with embedded assessment — tailored to
                learner needs, curriculum requirements, and local context.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4, alignItems: 'stretch' }}>
                {/* Card 1 — Two ways to use LearnCreator */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={CompareArrowsOutlinedIcon} title="Two ways to use LearnCreator" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {WAYS_TO_USE.map((way) => (
                            <Box key={way.title} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '14.5px', fontWeight: 700, color: TEXT, mb: 0.75 }}>
                                    {way.title}
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7, mb: 1.5 }}>
                                    {way.desc}
                                </Typography>
                                {targetOf(way) ? (
                                    <Button sx={{ ...primaryButtonSx, alignSelf: 'flex-end' }} onClick={() => history.push(targetOf(way))}>
                                        {isSchool && way.schoolCta ? way.schoolCta : way.cta}
                                    </Button>
                                ) : (
                                    <Tooltip title="Coming soon" arrow>
                                        <span style={{ alignSelf: 'flex-end' }}>
                                            <Button variant="outlined" disabled sx={outlineButtonSx}>
                                                {way.cta}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Card 2 — What you can customize */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={TuneOutlinedIcon} title="What you can customize" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {WHAT_YOU_CAN_CUSTOMIZE.map((item) => (
                            <Box key={item.title}>
                                <Typography sx={{ fontSize: '14.5px', fontWeight: 700, color: TEXT, mb: 0.75 }}>
                                    {item.title}
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                                    {item.desc}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Card 3 — What's included */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={WidgetsOutlinedIcon} title="What's included" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3 }}>
                        {WHATS_INCLUDED.map(({ title, desc }) => (
                            <Box key={title}>
                                <Typography sx={{ fontSize: '14.5px', fontWeight: 700, color: TEXT, mb: 0.75 }}>
                                    {title}
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                                    {desc}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 'auto' }}>
                        {/* Opens Solutions > LearnCreator > Preview, the same page as the
                            sidebar row (LA-PW,SNW,SW,TW-20Oct-02). That page's content
                            comes from Dr. Park (Ian Ryu); it shows "Coming soon" until then. */}
                        <Button variant="outlined" sx={outlineButtonSx} onClick={() => history.push('/dq_pro/solutions/learncreator/preview')}>
                            Preview Learn Creator
                        </Button>
                        <Button variant="outlined" sx={outlineButtonSx} onClick={() => history.push('/dq_pro/learning/program/list')}>
                            Browse templates
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default SolutionLearnCreatorOverviewPage;
